import { useEffect, useRef } from 'react';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { playArrivalTone } from '../lib/notificationSound';
import { readSoundEnabled } from '../lib/soundPrefs';

/**
 * Plays the arrival tone when the store records an announceable transition.
 *
 * ⚠️ Driven by `arrivalSeq`, the same counter the ripple uses, so the sound and the animation can
 * never disagree about what happened. Keyed on the COUNTER rather than on the tone: two failures in
 * a row carry the same tone, and an effect watching the tone alone would fire once.
 *
 * ⚠️ The preference is read at PLAY TIME, not subscribed to. It changes about once in a user's life,
 * from a switch two components away, and subscribing would re-run this effect on unrelated renders.
 * The cost of reading late is that a user who mutes mid-chime hears that one out — which is what
 * muting anything does.
 */
export function useArrivalChime(): void {
  const arrivalSeq = useNotificationStore((s) => s.arrivalSeq);
  const arrivalTone = useNotificationStore((s) => s.arrivalTone);
  const userId = useAuthStore((s) => s.user?.userId ?? null);

  // ⛔ Seeded with the CURRENT value, not 0. This hook mounts with the shell, and a remount — a
  // route change that unmounts the rail, React 18 strict-mode double-invoke in dev — would
  // otherwise replay the last transition's tone at someone for something that happened minutes ago.
  const lastPlayed = useRef(arrivalSeq);

  useEffect(() => {
    if (arrivalSeq === lastPlayed.current) return;
    lastPlayed.current = arrivalSeq;

    if (arrivalSeq === 0 || !arrivalTone) return;
    if (!readSoundEnabled(userId)) return;

    playArrivalTone(arrivalTone);
  }, [arrivalSeq, arrivalTone, userId]);
}
